import {
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type { GPUInstanceType } from "types/InstanceTypes";
import {
  getInstanceTypeForCloudCredential,
  getPublicInstanceTypes,
} from "./api";

const instanceTypeStaleTimeMs = 5 * 60 * 1000;

export const instanceTypeKeys = {
  all: ["devplane", "instance-types"] as const,
  publicList: () => [...instanceTypeKeys.all, "public"] as const,
  detail: (
    instanceType: string,
    cloudCredentialId: string,
    location?: string | null,
  ) =>
    [
      ...instanceTypeKeys.all,
      "detail",
      instanceType,
      cloudCredentialId,
      location ?? null,
    ] as const,
};

export function publicInstanceTypesQueryOptions() {
  return {
    queryKey: instanceTypeKeys.publicList(),
    queryFn: getPublicInstanceTypes,
    staleTime: instanceTypeStaleTimeMs,
  };
}

type InstanceTypeQueryKey = ReturnType<typeof instanceTypeKeys.detail>;

export function instanceTypeQueryOptions(
  instanceType: string,
  cloudCredentialId: string,
  location?: string | null,
): UseQueryOptions<
  GPUInstanceType | null,
  Error,
  GPUInstanceType | null,
  InstanceTypeQueryKey
> {
  return {
    queryKey: instanceTypeKeys.detail(
      instanceType,
      cloudCredentialId,
      location,
    ),
    queryFn: ({ signal }: QueryFunctionContext<InstanceTypeQueryKey>) =>
      getInstanceTypeForCloudCredential(
        instanceType,
        cloudCredentialId,
        { signal },
        location,
      ),
    enabled: Boolean(instanceType && cloudCredentialId),
    staleTime: instanceTypeStaleTimeMs,
  };
}

export function useInstanceTypeQuery(
  instanceType: string,
  cloudCredentialId: string,
  location?: string | null,
) {
  return useQuery(
    instanceTypeQueryOptions(instanceType, cloudCredentialId, location),
  );
}
